import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminActionModal } from '../../../components/admin-shell/AdminActionModal';
import { AdminShell } from '../../../components/admin-shell/AdminShell';
import { useAuth } from '../../../lib/auth-context';
import { showToast } from '../../../lib/toast';
import { adminGet, adminPost } from '../_api';

type UserDetail = {
  user: {
    id: string;
    email: string;
    display_name: string;
    role: 'artist' | 'host' | 'admin';
    // Wave 4L — `pending_close` is added once packages/shared/state-machines/user.ts gains the state.
    status: 'pending' | 'active' | 'suspended' | 'deleted' | 'pending_close';
    is_admin: boolean;
    locale: string;
    timezone: string;
    email_verified_at: string | null;
    last_login_at: string | null;
    suspension_until: string | null;
    created_at: string;
    updated_at: string;
  };
  artist_profile: {
    id: string;
    slug: string;
    published: boolean;
    verified_at: string | null;
  } | null;
  venue_account: {
    id: string;
    display_name: string;
    country: string;
    currency: string;
    status: string;
  } | null;
  venues: Array<{ id: string; name: string; slug: string; status: string; city: string; country: string }>;
  sessions: Array<{
    id: string;
    created_at: string;
    last_seen_at: string;
    expires_at: string;
    revoked_at: string | null;
    user_agent: string | null;
  }>;
};

type SessionRow = {
  id: string;
  user_agent: string | null;
  ip_hash_truncated: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  revoked_at: string | null;
};

type SessionsResponse = { sessions: SessionRow[] };

export const Route = createFileRoute('/admin/users/$id')({
  component: AdminUserDetail,
});

type DataExportRow = {
  id: string;
  user_id: string;
  scope: 'all' | 'profile_only' | 'messages_only';
  state: 'pending' | 'approved' | 'building' | 'ready' | 'expired' | 'cancelled';
  requested_at: string;
  approved_at: string | null;
  ready_at: string | null;
  expires_at: string | null;
};

type DataExportListResponse = {
  items: DataExportRow[];
  total: number;
  limit: number;
  offset: number;
};

function AdminUserDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => adminGet<UserDetail>(`/api/admin/users/${id}`),
  });

  const exportsQ = useQuery({
    queryKey: ['admin', 'data-exports', id],
    queryFn: async () => {
      try {
        return await adminGet<DataExportListResponse>(
          `/api/admin/data-exports?user_id=${encodeURIComponent(id)}`,
        );
      } catch {
        // Not mounted yet, or schema missing — render an empty list.
        return { items: [], total: 0, limit: 25, offset: 0 } satisfies DataExportListResponse;
      }
    },
  });

  const approveExport = useMutation({
    mutationFn: (exportId: string) =>
      adminPost(`/api/admin/data-exports/${exportId}/approve`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'data-exports', id] }),
  });
  const cancelExport = useMutation({
    mutationFn: (exportId: string) =>
      adminPost(`/api/admin/data-exports/${exportId}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'data-exports', id] }),
  });

  const [suspendOpen, suspendCtl] = useDisclosure(false);
  const [restoreOpen, restoreCtl] = useDisclosure(false);
  const [reason, setReason] = useState('');

  const suspend = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/users/${id}/suspend`, {
        user_id: id,
        reason,
        share_with_user: true,
      }),
    onSuccess: () => {
      suspendCtl.close();
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'users', id] });
    },
  });

  const restore = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/users/${id}/restore`, {
        user_id: id,
        ...(reason.trim().length > 0 ? { reason } : {}),
      }),
    onSuccess: () => {
      restoreCtl.close();
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'users', id] });
    },
  });

  // ---- Admin access (double-confirmation via typed email) ------------------
  const [promoteOpen, promoteCtl] = useDisclosure(false);
  const [revokeOpen, revokeCtl] = useDisclosure(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [adminActionError, setAdminActionError] = useState<string | null>(null);

  function closeAdminModals() {
    promoteCtl.close();
    revokeCtl.close();
    setConfirmEmail('');
    setAdminActionError(null);
  }

  const promoteAdmin = useMutation({
    mutationFn: () =>
      adminPost<{ ok: boolean; granted: boolean }>(`/api/admin/users/${id}/promote-admin`, {
        confirm_email: confirmEmail.trim(),
      }),
    onSuccess: (res) => {
      closeAdminModals();
      void qc.invalidateQueries({ queryKey: ['admin', 'users', id] });
      showToast({
        kind: 'success',
        body: res.granted ? 'Admin access granted.' : 'They already had admin access.',
      });
    },
    onError: (e) => setAdminActionError(e instanceof Error ? e.message : 'Could not grant admin access.'),
  });

  const revokeAdmin = useMutation({
    mutationFn: () =>
      adminPost<{ ok: boolean; revoked: boolean }>(`/api/admin/users/${id}/revoke-admin`, {
        confirm_email: confirmEmail.trim(),
      }),
    onSuccess: (res) => {
      closeAdminModals();
      void qc.invalidateQueries({ queryKey: ['admin', 'users', id] });
      showToast({
        kind: 'success',
        body: res.revoked ? 'Admin access revoked.' : 'They were not an admin.',
      });
    },
    onError: (e) => setAdminActionError(e instanceof Error ? e.message : 'Could not revoke admin access.'),
  });

  const sessionsQuery = useQuery({
    queryKey: ['admin', 'users', id, 'sessions'],
    queryFn: () => adminGet<SessionsResponse>(`/api/admin/users/${id}/sessions`),
  });

  const revokeSession = useMutation({
    mutationFn: (sessionId: string) =>
      adminPost<{ ok: boolean; revoked: boolean }>(
        `/api/admin/users/${id}/sessions/${sessionId}/revoke`,
        {},
      ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users', id, 'sessions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'users', id] });
      showToast({
        kind: 'success',
        body: res.revoked ? 'Session revoked.' : 'Session was already revoked.',
      });
    },
    onError: () => {
      showToast({ kind: 'error', body: 'Could not revoke session.' });
    },
  });

  if (isLoading) {
    return (
      <AdminShell breadcrumb="Users">
        <Loader size="sm" />
      </AdminShell>
    );
  }
  if (error || !data) {
    return (
      <AdminShell breadcrumb="Users">
        <Alert color="red">Could not load this user.</Alert>
      </AdminShell>
    );
  }

  const { user } = data;
  const isSelf = currentUser?.id === user.id;
  const emailMatches = confirmEmail.trim().toLowerCase() === user.email.toLowerCase();

  return (
    <AdminShell breadcrumb={`Users / ${user.email}`}>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <Anchor component={Link} to="/admin/users" size="sm">
              ← All users
            </Anchor>
            <Title order={2}>{user.display_name}</Title>
            <Group gap="xs">
              <Text c="dimmed">{user.email}</Text>
              <Badge variant="light">{user.role}</Badge>
              {user.is_admin ? (
                <Badge variant="filled" color="grape">
                  admin
                </Badge>
              ) : null}
              <Badge variant="light" color={statusColor(user.status)}>
                {user.status}
              </Badge>
            </Group>
          </Stack>
          <Group>
            {user.is_admin ? (
              <Button
                color="grape"
                variant="light"
                onClick={() => {
                  setConfirmEmail('');
                  setAdminActionError(null);
                  revokeCtl.open();
                }}
                disabled={isSelf}
                title={isSelf ? 'You cannot revoke your own admin access.' : undefined}
              >
                Revoke admin
              </Button>
            ) : (
              <Button
                color="grape"
                variant="light"
                onClick={() => {
                  setConfirmEmail('');
                  setAdminActionError(null);
                  promoteCtl.open();
                }}
              >
                Promote to admin
              </Button>
            )}
            {user.status === 'active' || user.status === 'pending' ? (
              <Button color="yellow" variant="light" onClick={suspendCtl.open}>
                Suspend
              </Button>
            ) : null}
            {user.status === 'suspended' ? (
              <Button color="tide" onClick={restoreCtl.open}>
                Restore
              </Button>
            ) : null}
          </Group>
        </Group>

        <Card withBorder padding="md">
          <Title order={5} mb="xs">
            Account
          </Title>
          <Stack gap={2}>
            <KV k="ID" v={user.id} />
            <KV k="Locale" v={user.locale} />
            <KV k="Timezone" v={user.timezone} />
            <KV k="Email verified" v={user.email_verified_at ? formatDate(user.email_verified_at) : 'Not verified'} />
            <KV k="Last login" v={user.last_login_at ? formatDate(user.last_login_at) : 'Never'} />
            <KV k="Joined" v={formatDate(user.created_at)} />
            {user.suspension_until ? <KV k="Suspended until" v={formatDate(user.suspension_until)} /> : null}
          </Stack>
        </Card>

        {data.artist_profile ? (
          <Card withBorder padding="md">
            <Title order={5} mb="xs">
              Artist profile
            </Title>
            <Stack gap={2}>
              <KV k="Slug" v={data.artist_profile.slug} />
              <KV k="Published" v={data.artist_profile.published ? 'Yes' : 'No'} />
              <KV
                k="Verified"
                v={data.artist_profile.verified_at ? formatDate(data.artist_profile.verified_at) : 'Not verified'}
              />
            </Stack>
          </Card>
        ) : null}

        {data.venue_account ? (
          <Card withBorder padding="md">
            <Title order={5} mb="xs">
              Venue account
            </Title>
            <Stack gap={2}>
              <KV k="Display name" v={data.venue_account.display_name} />
              <KV k="Country" v={data.venue_account.country} />
              <KV k="Currency" v={data.venue_account.currency} />
              <KV k="Status" v={data.venue_account.status} />
            </Stack>
            {data.venues.length > 0 ? (
              <>
                <Text size="sm" c="dimmed" mt="md">
                  Venues
                </Text>
                <Table>
                  <Table.Tbody>
                    {data.venues.map((v) => (
                      <Table.Tr key={v.id}>
                        <Table.Td>{v.name}</Table.Td>
                        <Table.Td>
                          {v.city}, {v.country}
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light">{v.status}</Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            ) : null}
          </Card>
        ) : null}

        <Card withBorder padding="md">
          <Title order={5} mb="xs">
            Data export requests
          </Title>
          {(exportsQ.data?.items.length ?? 0) === 0 ? (
            <Text c="dimmed" size="sm">
              No export requests on record.
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Requested</Table.Th>
                  <Table.Th>Scope</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Available until</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(exportsQ.data?.items ?? []).map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>{formatDate(row.requested_at)}</Table.Td>
                    <Table.Td>{row.scope.replace('_', ' ')}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={exportColor(row.state)}>
                        {row.state}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {row.expires_at ? formatDate(row.expires_at) : '–'}
                    </Table.Td>
                    <Table.Td>
                      {row.state === 'pending' ? (
                        <Group gap="xs" wrap="nowrap">
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => approveExport.mutate(row.id)}
                            loading={approveExport.isPending}
                          >
                            Approve & build
                          </Button>
                          <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => cancelExport.mutate(row.id)}
                            loading={cancelExport.isPending}
                          >
                            Cancel
                          </Button>
                        </Group>
                      ) : row.state === 'approved' ? (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="gray"
                          onClick={() => cancelExport.mutate(row.id)}
                          loading={cancelExport.isPending}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Card withBorder padding="md">
          <Group justify="space-between" align="center" mb="xs">
            <Title order={5}>Sessions</Title>
            {sessionsQuery.isFetching ? <Loader size="xs" /> : null}
          </Group>
          {sessionsQuery.error ? (
            <Alert color="red">Could not load sessions.</Alert>
          ) : (
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Device</Table.Th>
                  <Table.Th>IP</Table.Th>
                  <Table.Th>Last seen</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(sessionsQuery.data?.sessions ?? []).map((s) => {
                  const isRevoked = s.revoked_at !== null;
                  const isExpired = new Date(s.expires_at).getTime() <= Date.now();
                  const isLive = !isRevoked && !isExpired;
                  return (
                    <Table.Tr key={s.id}>
                      <Table.Td>
                        <Tooltip label={s.user_agent ?? 'Unknown user agent'} multiline w={320}>
                          <Text size="sm">{shortenUserAgent(s.user_agent)}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {s.ip_hash_truncated ?? '–'}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatDate(s.last_seen_at)}</Table.Td>
                      <Table.Td>{formatDate(s.created_at)}</Table.Td>
                      <Table.Td>
                        {isRevoked ? (
                          <Badge variant="light" color="gray">
                            Revoked
                          </Badge>
                        ) : isExpired ? (
                          <Badge variant="light" color="gray">
                            Expired
                          </Badge>
                        ) : (
                          <Badge variant="light" color="tide">
                            Active
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {isLive ? (
                          <Button
                            size="xs"
                            variant="light"
                            color="yellow"
                            loading={
                              revokeSession.isPending && revokeSession.variables === s.id
                            }
                            onClick={() => revokeSession.mutate(s.id)}
                          >
                            Revoke
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {sessionsQuery.data && sessionsQuery.data.sessions.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed">No sessions on record.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <AdminActionModal
        opened={suspendOpen}
        onClose={suspendCtl.close}
        title="Suspend account"
        onConfirm={() => suspend.mutate()}
        confirmLabel="Suspend"
        confirmColor="yellow"
        loading={suspend.isPending}
        confirmDisabled={reason.trim().length < 10}
      >
        <Textarea
          label="Reason"
          description="At least 10 characters. Logged in audit; shared with the user."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
        />
      </AdminActionModal>

      <AdminActionModal
        opened={restoreOpen}
        onClose={restoreCtl.close}
        title="Restore account"
        onConfirm={() => restore.mutate()}
        confirmLabel="Restore"
        confirmColor="tide"
        loading={restore.isPending}
      >
        <Textarea
          label="Note (optional)"
          description="Logged in audit; not shared with the user."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={2}
        />
      </AdminActionModal>

      <AdminActionModal
        opened={promoteOpen}
        onClose={closeAdminModals}
        title="Promote to admin"
        onConfirm={() => {
          setAdminActionError(null);
          promoteAdmin.mutate();
        }}
        confirmLabel="Grant admin access"
        confirmColor="grape"
        loading={promoteAdmin.isPending}
        confirmDisabled={!emailMatches}
        error={adminActionError}
      >
        <Text size="sm">
          This gives <strong>{user.display_name}</strong> full access to the admin console —
          every user, venue, dispute, and setting. Only grant it to people you trust with the
          whole platform.
        </Text>
        <TextInput
          label="Confirm the account email to continue"
          description={`Type ${user.email}`}
          placeholder={user.email}
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.currentTarget.value)}
          error={confirmEmail.length > 0 && !emailMatches ? "That doesn't match." : null}
        />
      </AdminActionModal>

      <AdminActionModal
        opened={revokeOpen}
        onClose={closeAdminModals}
        title="Revoke admin access"
        onConfirm={() => {
          setAdminActionError(null);
          revokeAdmin.mutate();
        }}
        confirmLabel="Revoke admin access"
        confirmColor="red"
        loading={revokeAdmin.isPending}
        confirmDisabled={!emailMatches}
        error={adminActionError}
      >
        <Text size="sm">
          This removes <strong>{user.display_name}</strong>&rsquo;s access to the admin console.
          They keep their account and can still sign in as a normal member.
        </Text>
        <TextInput
          label="Confirm the account email to continue"
          description={`Type ${user.email}`}
          placeholder={user.email}
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.currentTarget.value)}
          error={confirmEmail.length > 0 && !emailMatches ? "That doesn't match." : null}
        />
      </AdminActionModal>
    </AdminShell>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <Group gap="xs">
      <Text c="dimmed" size="sm" style={{ minWidth: 120 }}>
        {k}
      </Text>
      <Text size="sm">{v}</Text>
    </Group>
  );
}

function statusColor(status: string): string {
  if (status === 'active') return 'tide';
  if (status === 'suspended') return 'yellow';
  if (status === 'pending_close') return 'red';
  if (status === 'deleted') return 'gray';
  return 'blue';
}

function exportColor(state: string): string {
  if (state === 'ready') return 'tide';
  if (state === 'pending' || state === 'building') return 'yellow';
  return 'gray';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Compress a User-Agent string into a one-line "Browser on OS" hint. The full
 * UA is rendered on hover; this is just the at-a-glance label that stops the
 * sessions table from spilling 200-char rows.
 */
function shortenUserAgent(ua: string | null): string {
  if (ua === null || ua.length === 0) return 'Unknown device';
  const browser =
    /Edg\//.exec(ua) !== null
      ? 'Edge'
      : /Firefox\//.exec(ua) !== null
        ? 'Firefox'
        : /Chrome\//.exec(ua) !== null
          ? 'Chrome'
          : /Safari\//.exec(ua) !== null
            ? 'Safari'
            : 'Browser';
  const os =
    /Windows NT/.exec(ua) !== null
      ? 'Windows'
      : /Mac OS X|Macintosh/.exec(ua) !== null
        ? 'macOS'
        : /iPhone|iPad|iPod/.exec(ua) !== null
          ? 'iOS'
          : /Android/.exec(ua) !== null
            ? 'Android'
            : /Linux/.exec(ua) !== null
              ? 'Linux'
              : 'Unknown';
  return `${browser} on ${os}`;
}
